import type { ElementType, HTMLAttributes, ReactNode } from 'react';

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'sh-xl'
  | 'sh-l'
  | 'sh-m'
  | 'sh-s'
  | 'sh-xs'
  | 'body-xl'
  | 'body-l'
  | 'body-m'
  | 'body-s'
  | 'body-xs'
  | 'body-xxs'
  | 'par-l'
  | 'par-m'
  | 'par-s'
  | 'par-xs'
  | 'st-xl'
  | 'st-l'
  | 'st-m'
  | 'st-s'
  | 'st-xs'
  | 'link-xl'
  | 'link-l'
  | 'link-m'
  | 'link-s'
  | 'link-xs'
  | 'heading-display'
  | 'heading-page'
  | 'section-large'
  | 'section-l'
  | 'section-medium'
  | 'section-small'
  | 'section-xsmall'
  | 'body-large'
  | 'body-medium'
  | 'body-small'
  | 'body-xsmall'
  | 'body-xxsmall'
  | 'paragraph-large'
  | 'paragraph-medium'
  | 'paragraph-small'
  | 'paragraph-xsmall'
  | 'link-large'
  | 'link-medium'
  | 'link-small'
  | 'link-xsmall';

const defaultElements: Record<TypographyVariant, ElementType> = {
  h1: 'h1',
  'heading-display': 'h1',
  h2: 'h2',
  'heading-page': 'h2',
  'sh-xl': 'h3',
  'section-large': 'h3',
  'sh-l': 'h4',
  'section-l': 'h4',
  'sh-m': 'h5',
  'section-medium': 'h5',
  'sh-s': 'h6',
  'section-small': 'h6',
  'sh-xs': 'p',
  'section-xsmall': 'p',
  'body-xl': 'p',
  'body-large': 'p',
  'body-l': 'p',
  'body-m': 'p',
  'body-medium': 'p',
  'body-s': 'p',
  'body-small': 'p',
  'body-xs': 'p',
  'body-xsmall': 'p',
  'body-xxs': 'p',
  'body-xxsmall': 'p',
  'par-l': 'p',
  'paragraph-large': 'p',
  'par-m': 'p',
  'paragraph-medium': 'p',
  'par-s': 'p',
  'paragraph-small': 'p',
  'par-xs': 'p',
  'paragraph-xsmall': 'p',
  'st-xl': 's',
  'st-l': 's',
  'st-m': 's',
  'st-s': 's',
  'st-xs': 's',
  'link-xl': 'a',
  'link-large': 'a',
  'link-l': 'a',
  'link-m': 'a',
  'link-medium': 'a',
  'link-s': 'a',
  'link-small': 'a',
  'link-xs': 'a',
  'link-xsmall': 'a',
};

interface TypographyProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  children: ReactNode;
  variant?: TypographyVariant;
}

export function Typography({
  as,
  children,
  className = '',
  variant = 'body-m',
  ...props
}: TypographyProps) {
  const Component = as ?? defaultElements[variant];

  return (
    <Component className={`ui-type ui-type-${variant} ${className}`.trim()} {...props}>
      {children}
    </Component>
  );
}
